// Auth client helpers.
//
// Auth strategy (post-rollback): real JWT in localStorage + Authorization
// Bearer header on every backend fetch. This works under any CORS configuration
// (including the production Emergent ingress which injects
// `Access-Control-Allow-Origin: *`). The backend additionally sets an httpOnly
// auth cookie for clients that want to opt in, but the front end no longer
// depends on it — keeping things robust against platform-level CORS quirks.

export const COOKIE_AUTH_MARKER = 'cookie-auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const isBackendRequest = (input) => {
  try {
    const url = typeof input === 'string' ? input : input?.url;
    if (!url) return false;
    if (BACKEND_URL && url.startsWith(BACKEND_URL)) return true;
    if (url.startsWith('/api/')) return true;
    return false;
  } catch {
    return false;
  }
};

const clearLocalAuth = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  } catch (err) {
    console.warn('[authClient] localStorage cleanup failed', err);
  }
};

const PUBLIC_PATHS = new Set([
  '/', '/login', '/register', '/registrieren', '/subscription',
  '/privacy', '/datenschutz', '/impressum', '/terms', '/agb',
  '/home-classic', '/forgot-password', '/passwort-vergessen',
  '/reset-password', '/passwort-zuruecksetzen', '/widerruf',
  '/withdrawal', '/widerrufsformular', '/withdrawal-form',
  '/partner/bewerben', '/affiliate/apply', '/features', '/funktionen',
]);

const handleUnauthorized = () => {
  clearLocalAuth();
  const path = window.location.pathname;
  if (path.startsWith('/admin')) return;
  if (PUBLIC_PATHS.has(path) || path.startsWith('/play/')) return;
  window.location.href = '/login?expired=true';
};

let installed = false;

// We still install a light fetch wrapper that:
//   - Detects 401 on backend calls and bounces the user to /login
//   - Does NOT add `credentials: 'include'` — the production ingress sets
//     `Access-Control-Allow-Origin: *` on some responses, which the browser
//     blocks when paired with credentialed requests. Bearer header works on
//     any CORS configuration so we sidestep the issue entirely.
export const installAuthFetch = () => {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const response = await originalFetch(input, init);

    if (response && response.status === 401 && isBackendRequest(input)) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isAuthEndpoint = url.includes('/api/auth/login')
        || url.includes('/api/auth/register')
        || url.includes('/api/auth/forgot-password')
        || url.includes('/api/auth/reset-password');
      if (!isAuthEndpoint) {
        handleUnauthorized();
      }
    }
    return response;
  };
};

export const serverLogout = async () => {
  // Best-effort cookie clear on the server; failures are non-fatal because
  // local cleanup is what matters for the JWT-based flow.
  try {
    if (BACKEND_URL) {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    }
  } catch (err) {
    console.warn('[authClient] server logout failed (non-fatal)', err);
  } finally {
    clearLocalAuth();
  }
};

export { clearLocalAuth };
